import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type {
  ElementSegmentId,
  StructuralElementId,
  StructuralElementTypeId,
  WallId,
} from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import { GEOMETRY_DECIMALS } from './room';
import {
  exactPolygonArea,
  isSimplePolygon,
  isValidOutline,
  polygonPerimeter,
  type Point,
} from './polygon';
import { TAKEOFF_ERROR } from './errors';
import type { ItemFactor } from './own-factor';
import type { SteelRate } from './steel';
import type { Wall, WallOwner } from './wall';

/**
 * Pilar e viga — a estrutura que ocupa o plano da parede (Fase 15).
 *
 * A Fase 14 mediu a parede inteira, e ela não é inteira: onde há concreto não há bloco. O
 * sistema cobrava alvenaria embaixo de toda viga e atrás de todo pilar, porque a
 * `Wall.height` é a altura **até a laje** — que é justamente onde a viga está.
 *
 * O elemento é objeto próprio, e o encontro dele com cada parede é um **trecho**
 * (ADR-115). É a mesma porta pela qual a parede entrou na Fase 14: ela não pertence a um
 * ambiente, ela registra quais ambientes separa. A viga não pertence à parede; ela
 * registra onde a cruza. Cadastrá-la em cada parede seria cadastrá-la três vezes — três
 * seções para manter iguais, e um dia em que só duas foram corrigidas.
 *
 * > **A regra em uma linha (RN-190):** o desconto é do trecho; o volume e a espala são do
 * > objeto.
 */

/**
 * Casas decimais do volume — **três**, e não as duas da geometria.
 *
 * Um pilar 20×40 de 2,90 dá 0,232 m³. Arredondado a duas casas ele vira 0,23, e o prédio
 * com quarenta pilares perde 0,8 m³ de concreto — quase um caminhão, sumido no
 * arredondamento. Área não tem esse problema porque os números são grandes; o metro
 * cúbico é pequeno, e a terceira casa é onde ele mora.
 */
export const VOLUME_DECIMALS = 3;

export type StructuralElementKind = 'PILAR' | 'VIGA';

export const STRUCTURAL_ELEMENT_KIND_LABEL: Readonly<Record<StructuralElementKind, string>> = {
  PILAR: 'Pilar',
  VIGA: 'Viga',
};

/**
 * Como o elemento encontra **esta** parede.
 *
 * Para a viga é literal: ou ela corre pelo plano da parede (paralela) ou o atravessa
 * (perpendicular), e a forma do desconto muda com isso (RN-183).
 *
 * Para o pilar, que é vertical e por isso está sempre no plano de qualquer parede que
 * encoste nele, a orientação diz **qual face a parede vê**: paralela é a largura,
 * perpendicular é a profundidade. É o que resolve o pilar de canto, que pertence a duas
 * paredes e mostra uma face diferente para cada uma (RN-189).
 */
export type ElementOrientation = 'PARALELA' | 'PERPENDICULAR';

export const ELEMENT_ORIENTATIONS: readonly ElementOrientation[] = ['PARALELA', 'PERPENDICULAR'];

/**
 * Como a escolha se chama na tela, que não é a mesma coisa para os dois.
 *
 * "Paralela" e "perpendicular" descrevem a viga e só a viga. O pilar é vertical: ele nunca
 * atravessa parede nenhuma, e o que a escolha decide é **qual face aquela parede vê** — a
 * pergunta que a obra faz olhando o pilar de canto. Oferecer a ele as palavras da viga
 * seria pedir uma resposta que não corresponde a nada no desenho.
 */
export function orientationLabel(
  kind: StructuralElementKind,
  orientation: ElementOrientation,
): string {
  if (kind === 'VIGA') {
    return orientation === 'PARALELA'
      ? 'Paralela — corre pelo plano da parede'
      : 'Perpendicular — atravessa a parede';
  }
  return orientation === 'PARALELA' ? 'A parede vê a largura' : 'A parede vê a profundidade';
}

/**
 * Para que lado da parede o elemento sobra.
 *
 * `A` e `B` são os lados da parede, os mesmos de `sideA` e `sideB` — na parede externa, o
 * lado B é a rua, e o que sobra para lá é fachada.
 */
export type AdvanceSide = 'A' | 'B' | 'AMBOS';

/**
 * O encontro do elemento com uma parede.
 *
 * É o trecho, e é ele que desconta. O mesmo pilar de canto tem dois trechos, um em cada
 * parede, e cada um tira da sua — cadastrado em uma só, a outra cobra bloco a mais
 * (RN-189).
 */
export interface ElementSegment {
  readonly id: ElementSegmentId;
  readonly elementId: StructuralElementId;
  readonly wallId: WallId;
  readonly orientation: ElementOrientation;
  /**
   * Quanto o elemento sobra além da face da parede, em metros. Ausente é **derivado**.
   *
   * Pilar de 20 em parede de 15 sobra 5 cm, e o cadastro já sabe disso: `segmentAdvance`
   * tira a sobra da seção menos a espessura da parede. Este campo é a **correção** de quem
   * mediu na obra e viu outra coisa — e `0` aqui é rente declarado, que vence a derivação.
   *
   * Rente não cria superfície nenhuma: a elevação continua sendo uma só, rebocada sobre
   * concreto em vez de sobre bloco (RN-186).
   */
  readonly advance?: number;
  /**
   * Para qual lado ele sobra (RN-185).
   *
   * `AMBOS` dá a **medida inteira a cada lado**, e não metade dela: o avanço digitado é o
   * que aquele ambiente vê, e repartir o número entre os dois cobraria meia espala de cada
   * um. É o mesmo princípio do reboco, que conta as duas faces da mesma parede.
   *
   * Ausente, o lado depende de quem respondeu o avanço: digitado, é `A`; derivado da seção,
   * é `AMBOS` — o elemento no eixo da parede, sobrando metade para cada ambiente.
   */
  readonly advanceSide?: AdvanceSide;
  /**
   * Quanto da corrida do elemento passa por esta parede, em metros.
   *
   * Só a viga paralela usa: é o comprimento da faixa que ela come. **Ausente é a parede
   * inteira**, que é o caso comum — a viga nasce em cima da parede e corre com ela.
   *
   * Não tem uso na perpendicular, que deixa a seção e não uma faixa, nem no pilar, cuja
   * corrida é vertical e vai da laje ao piso.
   */
  readonly length?: number;
  /**
   * Onde, ao longo desta parede, está o **centro** do elemento — em metros a partir do
   * início dela.
   *
   * O trecho diz *em que* parede a peça está; sem este campo ele não dizia *onde nela*, e
   * o desenho tinha de adivinhar: pilar no meio da parede, viga parcial no meio do vão.
   * Quem desenhava um pilar na ponta de uma parede de 4 m o via saltar dois metros — o
   * desenho não estava errado por conta própria, ele estava devolvendo a única coisa que o
   * cadastro sabia.
   *
   * **Ausente continua sendo legítimo**: é o elemento cadastrado sem desenho, e para ele o
   * palpite centralizado segue valendo, marcado como palpite. Presente, manda — inclusive
   * sobre o encontro de duas paredes, porque quem desenhou viu onde a peça está.
   */
  readonly offset?: number;
  /**
   * Quanto o **centro** do elemento está fora do eixo desta parede, em metros, para o lado
   * esquerdo de quem anda do início ao fim dela — negativo é o lado direito.
   *
   * `offset` diz onde a peça está **ao longo** da parede; sem este campo o desenho a
   * recolocava centrada no eixo, e o pilar traçado encostado por dentro do ambiente saltava
   * meia espessura no instante em que era cadastrado. Ausente é o eixo — o palpite de antes.
   */
  readonly lateral?: number;
}

export interface StructuralElement {
  readonly id: StructuralElementId;
  readonly owner: WallOwner;
  readonly kind: StructuralElementKind;
  readonly name?: string;
  /**
   * Metros. Pilar: a largura em planta. Viga: a largura dela — a espessura.
   *
   * É o primeiro número de "20 × 40" e de "15 × 50", na ordem em que a obra fala.
   */
  readonly sectionWidth: number;
  /** Metros. Pilar: a profundidade em planta. Viga: a **altura** da seção. */
  readonly sectionDepth: number;
  /**
   * A corrida do elemento, em metros: a altura do pilar, o comprimento da viga.
   *
   * É do **objeto**, e não do trecho (RN-190): o pilar de canto tem uma altura só, e
   * somá-la por trecho contaria a mesma corrida duas vezes.
   *
   * No pilar é o que vale, e é digitado. Na **viga** ele é o valor de partida: a corrida
   * que conta é a soma dos trechos — `elementRun` —, porque é ela que não pode divergir de
   * onde a viga passa. Vale sozinho só enquanto a viga não tem trecho nenhum.
   */
  readonly length: number;
  /**
   * O trecho da viga que não cruza parede alguma, em metros (fatia 4).
   *
   * O vão livre é um **trecho como os outros**, e entra no comprimento total pelo mesmo
   * caminho: ele não desconta bloco — não há parede ali para descontar —, mas tem volume e
   * tem espala. Uma viga de 7 m que cruza duas paredes de 4 e 3 não tem vão livre; a mesma
   * viga com 8 m tem 1 m no vazio, e esse metro é concreto que alguém vai comprar.
   */
  readonly freeSpan?: number;
  /**
   * O contorno do elemento na planta, em metros (06/10/2026). Ausente é o elemento de antes:
   * retângulo de seção `sectionWidth × sectionDepth`, posto pelos trechos nas paredes.
   *
   * Presente, ele manda na posição e na forma — é o que deixa existir o **pilar sem parede**
   * (nenhum trecho, e o desenho sabe onde ele está) e o **pilar e a viga em polígono**, como
   * o bloco. As contas passam a sair dele (`elementVolume`, `elementFaces`, `elementBottom`,
   * `elementRun`); a seção digitada continua guardada — o retângulo envolvente no pilar, a
   * largura e a altura da seção na viga —, porque a viga em polígono ainda precisa da largura
   * para saber quanto do contorno é ponta e da altura para o volume.
   *
   * Os trechos continuam, quando a peça encosta em parede: é por eles que a alvenaria
   * desconta (RN-190). O contorno não desconta nada sozinho.
   */
  readonly outline?: readonly Point[];
  /**
   * O tipo do pilar ou da viga (Fase 42, RN-562): a composição que a peça segue. Do mesmo elemento
   * — o pilar aponta tipo de pilar, a viga, tipo de viga. Ausente: o índice da obra.
   */
  readonly elementTypeId?: StructuralElementTypeId;
  /**
   * O fator próprio do card (Fase 42, RN-563), por cima do tipo ou do índice da obra. Vazio ou
   * ausente: nenhum.
   */
  readonly factors?: readonly ItemFactor[];
  /**
   * A taxa de aço própria desta peça, kg/m³ por bitola (Fase 42, etapa 2 — G2; RN-568). Vale
   * inteira, por cima da do tipo e da disciplina. Ausente: o rascunho de antes, que não a
   * conhece; vazia: nenhuma, e a peça volta à do tipo ou à da disciplina.
   */
  readonly steelRates?: readonly SteelRate[];
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateStructuralElementInput {
  readonly id: StructuralElementId;
  readonly owner: WallOwner;
  readonly kind: StructuralElementKind;
  readonly name?: string;
  readonly sectionWidth: number;
  readonly sectionDepth: number;
  readonly length: number;
  readonly freeSpan?: number;
  readonly outline?: readonly Point[];
  readonly notes?: string;
  readonly now: string;
}

/**
 * A largura que o traço da viga mostra, em metros.
 *
 * Uma faixa de largura `t` e corrida `c` tem área `t × c` e perímetro `2c + 2t`; das duas
 * contas sai `t² − (perímetro / 2)·t + área = 0`, e a largura é a raiz menor. Vale para a
 * viga reta e para a em L de espessura constante — que é o que a obra desenha. Num traço
 * gordo demais para ser faixa (a conta não fecha), vale a metade do semiperímetro.
 */
export function outlineBeamWidth(outline: readonly Point[]): number {
  const meio = polygonPerimeter(outline) / 2;
  const area = exactPolygonArea(outline);
  const folga = Math.max(0, meio * meio - 4 * area);
  return (meio - Math.sqrt(folga)) / 2;
}

/** A largura digitada pode passar a do traço em 5%: é a folga do clique, não a do erro. */
const FOLGA_DA_LARGURA = 1.05;

/**
 * O contorno serve? Ao menos três pontos, lados que não somem, que não se cruzam e área maior
 * que zero. Na viga, o perímetro precisa passar das duas pontas (`2 × largura`) e a largura
 * digitada não pode passar da que o traço desenhou (`outlineBeamWidth`): 0,59 × 0,20 com
 * largura 0,50 somaria o concreto de uma viga que ninguém desenhou.
 */
export function checkElementOutline(
  kind: StructuralElementKind,
  outline: readonly Point[],
  sectionWidth: number,
): Result<readonly Point[], DomainError> {
  const rotulo = kind === 'PILAR' ? 'do pilar' : 'da viga';
  const semArea = err(
    domainError(
      TAKEOFF_ERROR.INVALID_DIMENSION,
      `Trace o contorno ${rotulo} na planta: ao menos três pontos, com área.`,
      { points: outline.length },
    ),
  );
  const finitos = outline.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (!finitos || !isValidOutline(outline)) return semArea;
  if (!isSimplePolygon(outline)) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_DIMENSION,
        `O contorno se cruza: trace ${kind === 'PILAR' ? 'o pilar' : 'a viga'} de novo, sem lados que se atravessem.`,
        { points: outline.length },
      ),
    );
  }
  if (exactPolygonArea(outline) <= 0) return semArea;
  if (kind === 'VIGA') {
    const doTraco = outlineBeamWidth(outline);
    if (sectionWidth > FOLGA_DA_LARGURA * doTraco) {
      return err(
        domainError(
          TAKEOFF_ERROR.INVALID_DIMENSION,
          `A largura digitada é maior que a do traço (${String(Math.round(doTraco * 100))} cm).`,
          { sectionWidth, traced: doTraco },
        ),
      );
    }
  }
  if (kind === 'VIGA' && polygonPerimeter(outline) <= 2 * sectionWidth) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_DIMENSION,
        'A largura da viga é maior que o contorno dela: confira a largura digitada.',
        { sectionWidth, perimeter: polygonPerimeter(outline) },
      ),
    );
  }
  return ok(outline.map((p) => ({ x: p.x, y: p.y })));
}

function positive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

export function createStructuralElement(
  input: CreateStructuralElementInput,
): Result<StructuralElement, DomainError> {
  if (!positive(input.sectionWidth) || !positive(input.sectionDepth)) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_DIMENSION,
        'A seção deve ter os dois lados maiores que zero.',
        {
          sectionWidth: input.sectionWidth,
          sectionDepth: input.sectionDepth,
        },
      ),
    );
  }
  if (!positive(input.length)) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_DIMENSION,
        input.kind === 'PILAR'
          ? 'A altura do pilar deve ser maior que zero.'
          : 'O comprimento da viga deve ser maior que zero.',
        { length: input.length },
      ),
    );
  }

  // Vão livre negativo é recusado; zero é a viga que não tem vazio nenhum, e é o comum.
  if (input.freeSpan !== undefined && !(Number.isFinite(input.freeSpan) && input.freeSpan >= 0)) {
    return err(
      domainError(TAKEOFF_ERROR.INVALID_DIMENSION, 'O vão livre não pode ser negativo.', {
        freeSpan: input.freeSpan,
      }),
    );
  }

  let outline: readonly Point[] | undefined;
  if (input.outline !== undefined) {
    const conferido = checkElementOutline(input.kind, input.outline, input.sectionWidth);
    if (!conferido.ok) return conferido;
    outline = conferido.value;
  }

  return ok({
    id: input.id,
    owner: input.owner,
    kind: input.kind,
    sectionWidth: input.sectionWidth,
    sectionDepth: input.sectionDepth,
    length: input.length,
    createdAt: input.now,
    updatedAt: input.now,
    ...(outline === undefined ? {} : { outline }),
    ...(input.freeSpan === undefined ? {} : { freeSpan: input.freeSpan }),
    ...(input.name === undefined ? {} : { name: input.name }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  });
}

export function createElementSegment(input: {
  readonly id: ElementSegmentId;
  readonly elementId: StructuralElementId;
  readonly wallId: WallId;
  readonly orientation: ElementOrientation;
  readonly length?: number;
  readonly advance?: number;
  readonly advanceSide?: AdvanceSide;
  readonly offset?: number;
  readonly lateral?: number;
}): Result<ElementSegment, DomainError> {
  if (input.length !== undefined && !positive(input.length)) {
    return err(
      domainError(TAKEOFF_ERROR.INVALID_DIMENSION, 'O trecho na parede deve ser maior que zero.', {
        length: input.length,
      }),
    );
  }
  // Avanço negativo é o único recusado: zero é rente, e rente é o caso comum.
  if (input.advance !== undefined && !(Number.isFinite(input.advance) && input.advance >= 0)) {
    return err(
      domainError(TAKEOFF_ERROR.INVALID_DIMENSION, 'O avanço não pode ser negativo.', {
        advance: input.advance,
      }),
    );
  }
  // Zero é legítimo — a peça no começo da parede —, e negativo não: fora da parede pela
  // origem é desenho que ninguém fez.
  if (input.offset !== undefined && !(Number.isFinite(input.offset) && input.offset >= 0)) {
    return err(
      domainError(TAKEOFF_ERROR.INVALID_DIMENSION, 'A posição na parede não pode ser negativa.', {
        offset: input.offset,
      }),
    );
  }
  return ok({
    id: input.id,
    elementId: input.elementId,
    wallId: input.wallId,
    orientation: input.orientation,
    ...(input.length === undefined ? {} : { length: input.length }),
    ...(input.advance === undefined ? {} : { advance: input.advance }),
    ...(input.advanceSide === undefined ? {} : { advanceSide: input.advanceSide }),
    ...(input.offset === undefined ? {} : { offset: input.offset }),
    // Os dois lados valem, então só o número sem sentido fica de fora.
    ...(input.lateral === undefined || !Number.isFinite(input.lateral)
      ? {}
      : { lateral: input.lateral }),
  });
}

/**
 * A sombra do elemento no plano da parede — o que ele tira de alvenaria (RN-182, RN-183).
 *
 * **É a sombra, e não a peça.** Um pilar 20×40 assentado com a face de 40 no plano tira
 * `0,40 × altura`; os 20 são profundidade, e profundidade dá volume, não tira bloco.
 * Descontar `0,20 × 0,40` — a seção — seria descontar a planta do pilar de uma elevação, e
 * o número sairia dezenas de vezes menor.
 *
 * As três formas:
 *
 * - **pilar**: `face no plano × altura` — a corrida é vertical e está sempre no plano, e a
 *   orientação escolhe qual das duas faces a parede vê;
 * - **viga paralela**: `trecho × altura da viga` — ela corre por cima e come uma faixa, e
 *   a alvenaria daquele trecho para embaixo dela;
 * - **viga perpendicular**: `largura × altura` — ela atravessa e deixa o retângulo da
 *   seção.
 *
 * Não limita o resultado ao tamanho da parede, pelo mesmo motivo que `wallNetArea` não
 * limita o vão: estrutura maior do que a parede é erro de digitação, e o lugar de recusá-la
 * é a validação, não a conta. Quem soma cuida do zero.
 */
export function segmentShadowArea(
  element: Pick<StructuralElement, 'kind' | 'sectionWidth' | 'sectionDepth' | 'length'>,
  segment: Pick<ElementSegment, 'orientation' | 'length'>,
  wall: Pick<Wall, 'length'>,
): number {
  if (element.kind === 'PILAR') {
    const noPlano =
      segment.orientation === 'PARALELA' ? element.sectionWidth : element.sectionDepth;
    return roundQuantity(noPlano * element.length, GEOMETRY_DECIMALS);
  }

  if (segment.orientation === 'PERPENDICULAR') {
    return roundQuantity(element.sectionWidth * element.sectionDepth, GEOMETRY_DECIMALS);
  }

  const trecho = segment.length ?? wall.length;
  return roundQuantity(trecho * element.sectionDepth, GEOMETRY_DECIMALS);
}

/**
 * Quanto da corrida da viga **esta** parede consome (fatia 4).
 *
 * Paralela, a viga corre pelo plano: consome o trecho, que por padrão é a parede inteira.
 * Perpendicular, ela atravessa: consome a **espessura** da parede, e nada mais — o resto
 * dela está do outro lado, em outro trecho ou no vazio.
 *
 * Parede sem espessura cadastrada consome zero, e não um palpite: a espessura é opcional
 * na Fase 14, e inventar 15 cm aqui somaria centímetros de concreto que ninguém mediu.
 */
export function runInWall(
  segment: Pick<ElementSegment, 'orientation' | 'length'>,
  wall: Pick<Wall, 'length' | 'thickness'>,
): number {
  if (segment.orientation === 'PERPENDICULAR') return wall.thickness ?? 0;
  return segment.length ?? wall.length;
}

/**
 * A corrida do elemento — a que **conta** (RN-190, fatia 4).
 *
 * No pilar é a altura digitada: ela é uma só, e somá-la por trecho contaria a mesma corrida
 * duas vezes no pilar de canto.
 *
 * Na viga é a **soma dos trechos**, inclusive o que não cruza parede alguma. Uma viga que
 * atravessa três paredes tem uma seção só e um comprimento só, e ele sai de onde ela passa
 * — não de um número digitado ao lado, que envelheceria calado no dia em que alguém
 * corrigisse o comprimento de uma das paredes.
 *
 * Sem trecho nenhum, vale o comprimento digitado: é a viga recém-criada, e zero diria que
 * ela não existe.
 */
export function elementRun(
  element: Pick<StructuralElement, 'kind' | 'length' | 'freeSpan'> &
    Partial<Pick<StructuralElement, 'outline' | 'sectionWidth'>>,
  segments: readonly ElementSegment[],
  walls: ReadonlyMap<WallId, Pick<Wall, 'length' | 'thickness'>>,
): number {
  if (element.kind === 'PILAR') return element.length;
  // A viga desenhada sabe a própria corrida: a média das duas laterais — o perímetro sem as
  // duas pontas, dividido por dois. No retângulo L × w é L; na viga em L é a linha do meio.
  if (element.outline !== undefined) {
    return outlineBeamRun(element.outline, element.sectionWidth ?? 0);
  }

  const dosTrechos = segments.reduce((total, segment) => {
    const wall = walls.get(segment.wallId);
    return wall === undefined ? total : total + runInWall(segment, wall);
  }, 0);
  const total = dosTrechos + (element.freeSpan ?? 0);

  return total > 0 ? roundQuantity(total, GEOMETRY_DECIMALS) : element.length;
}

/**
 * O concreto do elemento (RN-188).
 *
 * `seção × corrida`, e é a primeira grandeza em m³ do quantitativo — todas as outras sete
 * são m² ou metro linear. Sai do **objeto**: somá-la por trecho contaria o pilar de canto
 * duas vezes (RN-190).
 *
 * A corrida vem por parâmetro porque na viga ela não está no objeto: é a soma dos trechos
 * (`elementRun`). Ausente, vale a digitada — que é a do pilar e a da viga sem trecho.
 */
export function elementVolume(
  element: Pick<StructuralElement, 'sectionWidth' | 'sectionDepth' | 'length'> &
    Partial<Pick<StructuralElement, 'kind' | 'outline'>>,
  run = element.length,
): number {
  if (element.outline !== undefined) {
    // O contorno é a seção em planta: o pilar sobe a altura, a viga desce a altura da seção.
    const altura = element.kind === 'VIGA' ? element.sectionDepth : run;
    return roundQuantity(exactPolygonArea(element.outline) * altura, VOLUME_DECIMALS);
  }
  return roundQuantity(element.sectionWidth * element.sectionDepth * run, VOLUME_DECIMALS);
}

/** A corrida da viga em polígono: `(perímetro − 2 × largura) / 2`, nunca abaixo de zero. */
export function outlineBeamRun(outline: readonly Point[], sectionWidth: number): number {
  return roundQuantity(
    Math.max(0, polygonPerimeter(outline) - 2 * sectionWidth) / 2,
    GEOMETRY_DECIMALS,
  );
}

/**
 * Quanto do elemento atravessa a **espessura** da parede, em metros.
 *
 * É a medida que decide se ele cabe no plano ou sobra para fora dele, e ela não é a mesma
 * que aparece na elevação: no pilar 20 × 40 deitado com a face de 40 no plano, quem
 * atravessa a parede são os 20. A orientação já diz qual face a parede vê (RN-189); esta é
 * a outra.
 *
 * A viga perpendicular devolve `undefined`: ela **atravessa** a parede em vez de deitar
 * nela, e o que sai do outro lado é corrida — outro trecho ou vão livre (fatia 4), não
 * sobra.
 */
export function thicknessAcrossWall(
  element: Pick<StructuralElement, 'kind' | 'sectionWidth' | 'sectionDepth'>,
  segment: Pick<ElementSegment, 'orientation'>,
): number | undefined {
  if (element.kind === 'PILAR') {
    return segment.orientation === 'PARALELA' ? element.sectionDepth : element.sectionWidth;
  }
  // Deitada no plano, a viga ocupa a espessura da parede com a largura dela.
  return segment.orientation === 'PARALELA' ? element.sectionWidth : undefined;
}

/**
 * O avanço que **vale**, e para que lado — digitado ou derivado.
 *
 * A pergunta que a obra faz olhando o desenho é "o pilar ultrapassa a parede?", e ela já
 * tem resposta no cadastro: **seção menos espessura da parede**. Pilar de 20 em parede de
 * 15 sobra 5 cm, e pedir que alguém digite esses 5 cm é pedir uma subtração que o sistema
 * sabe fazer — e que envelhece calada no dia em que a parede de 15 virar de 20.
 *
 * Digitado vence, como a área do ambiente vence o contorno (RN-157): quem mediu na obra
 * viu algo que o cadastro não sabe. E `advance: 0` digitado é **rente declarado**, não
 * ausência — por isso a distinção é entre `undefined` e zero, e não entre zero e o resto.
 *
 * Duas coisas não se derivam, e as duas devolvem rente em vez de um palpite:
 *
 * - **parede sem espessura cadastrada** — a espessura é opcional na Fase 14, e inventar
 *   15 cm aqui criaria espala que ninguém mediu (é a mesma escolha de `runInWall`);
 * - **viga perpendicular** — ela atravessa, e não sobra.
 *
 * Sem lado declarado, a sobra derivada nasce **centrada**: o elemento está no eixo da
 * parede e cada ambiente vê metade dela. Escolher um lado sozinho seria decidir, por quem
 * desenhou, para dentro de qual quarto o pilar cresce.
 */
export function segmentAdvance(
  element: Pick<StructuralElement, 'kind' | 'sectionWidth' | 'sectionDepth'>,
  segment: Pick<ElementSegment, 'orientation' | 'advance' | 'advanceSide'>,
  wall: Pick<Wall, 'thickness'>,
): { readonly advance: number; readonly side: AdvanceSide; readonly derived: boolean } {
  if (segment.advance !== undefined) {
    return { advance: segment.advance, side: segment.advanceSide ?? 'A', derived: false };
  }

  const rente = { advance: 0, side: segment.advanceSide ?? 'A', derived: false } as const;
  const atravessa = thicknessAcrossWall(element, segment);
  if (atravessa === undefined || wall.thickness === undefined) return rente;

  const sobra = roundQuantity(atravessa - wall.thickness, GEOMETRY_DECIMALS);
  if (sobra <= 0) return rente;

  const lado = segment.advanceSide ?? 'AMBOS';
  // Metade **sem arredondar**: 2,5 cm arredondado a duas casas viraria 3, e os dois lados
  // somariam 6 onde o pilar sobra 5. Quem arredonda é a área, no fim da conta.
  return { advance: lado === 'AMBOS' ? sobra / 2 : sobra, side: lado, derived: true };
}

/**
 * Quanto o elemento sobra para **este** lado da parede, em metros (RN-185).
 *
 * Sem avanço nenhum, ou com ele declarado para o outro lado, é zero — e zero é rente, que
 * é o caso comum e o que não muda número nenhum. `AMBOS` devolve a medida inteira aos dois
 * lados, e não metade dela: o avanço é o que **aquele** ambiente vê.
 */
export function advanceTo(
  element: Pick<StructuralElement, 'kind' | 'sectionWidth' | 'sectionDepth'>,
  segment: Pick<ElementSegment, 'orientation' | 'advance' | 'advanceSide'>,
  wall: Pick<Wall, 'thickness'>,
  side: 'A' | 'B',
): number {
  const { advance, side: lado } = segmentAdvance(element, segment, wall);
  if (advance <= 0) return 0;
  return lado === 'AMBOS' || lado === side ? advance : 0;
}

/**
 * Quantas superfícies o avanço cria naquele ambiente.
 *
 * O pilar sobra para dentro e mostra **duas** faixas verticais, uma de cada lado dele. A
 * viga sobra por baixo e mostra **uma**, a barriga — em cima dela está a laje, que não se
 * reboca pela parede.
 *
 * É o mesmo número nas duas contas do avanço, e é por isso que ele mora aqui e não em cada
 * uma: o revestimento cobra essas faixas em **m²** (`segmentFaceArea`) e a espala cobra as
 * mesmas faixas em **metro linear** (`elementEspala`). Duas contagens separadas
 * discordariam no dia em que alguém mexesse numa só.
 */
function facesDoAvanco(kind: StructuralElementKind): number {
  return kind === 'PILAR' ? 2 : 1;
}

/**
 * O revestimento que o elemento acrescenta a um lado da parede (RN-185, RN-186).
 *
 * A conta é **desconta e devolve**: o plano perde a sombra do elemento, e o elemento
 * devolve a superfície que ele de fato oferece àquele ambiente. Rente, a devolução é
 * exatamente a sombra e nada muda — o concreto troca de material, não de necessidade
 * (RN-186). Sobrando para dentro, a devolução traz junto as faixas que o avanço criou, e
 * elas vão **inteiras** para cada lado em que ele aparece.
 *
 * As duas parcelas voltam separadas, e não já somadas, porque é assim que a regra se lê na
 * obra — e porque a fatia que vier depois pode precisar descontar sem devolver o mesmo.
 * Rente, elas se anulam de propósito: **é a anulação que é o comportamento certo**.
 *
 * A corrida do pilar entra limitada ao pé-direito: a face mede até o forro, e um pilar que
 * sobe até a laje ofereceria a ela um pedaço que ela nunca teve (RN-162).
 */
export function segmentFaceArea(
  element: Pick<StructuralElement, 'kind' | 'sectionWidth' | 'sectionDepth' | 'length'>,
  segment: Pick<ElementSegment, 'orientation' | 'length' | 'advance' | 'advanceSide'>,
  wall: Pick<Wall, 'length' | 'thickness'>,
  side: 'A' | 'B',
  ceilingHeight: number,
): { readonly discount: number; readonly given: number } {
  const sombra = segmentShadowArea(element, segment, wall);
  const avanco = advanceTo(element, segment, wall, side);
  if (avanco === 0) return { discount: sombra, given: sombra };

  // A corrida que aparece no ambiente: o pilar sobe até o forro, a viga corre o trecho.
  const corrida =
    element.kind === 'PILAR'
      ? Math.min(element.length, ceilingHeight)
      : (segment.length ?? wall.length);
  const doAvanco = roundQuantity(facesDoAvanco(element.kind) * avanco * corrida, GEOMETRY_DECIMALS);

  return { discount: sombra, given: roundQuantity(sombra + doAvanco, GEOMETRY_DECIMALS) };
}

/**
 * Quantas faces de espala este trecho cria **neste** lado da parede (RN-187).
 *
 * Rente não cria nenhuma: sem avanço não há quina para arrematar, e a elevação segue
 * sendo uma só, rebocada sobre concreto em vez de sobre bloco (RN-186). Avançando, são as
 * mesmas faixas que o revestimento cobra em m² — duas no pilar, uma na viga.
 */
export function espalaFaces(
  element: Pick<StructuralElement, 'kind' | 'sectionWidth' | 'sectionDepth'>,
  segment: Pick<ElementSegment, 'orientation' | 'advance' | 'advanceSide'>,
  wall: Pick<Wall, 'thickness'>,
  side: 'A' | 'B',
): number {
  return advanceTo(element, segment, wall, side) > 0 ? facesDoAvanco(element.kind) : 0;
}

/**
 * O metro linear de espala do elemento (RN-187).
 *
 * **A corrida vezes as faces expostas** — decidido com o cliente em 15/09/2026, medindo um
 * pilar de canto de verdade. A conta é por _face_, e não por lado: um pilar de 2,60 m que
 * avança para dentro de um ambiente mostra duas quinas e dá **5,20 m**; o pilar de canto,
 * que avança para dentro de dois ambientes por paredes diferentes, mostra quatro e dá
 * **10,40 m**. A viga mostra uma face por lado — a barriga —, e dá a corrida dela.
 *
 * O que **não** se soma é a corrida (RN-190): o pilar de canto tem uma altura só, e somá-la
 * por trecho contaria a mesma subida duas vezes. O que acumula por trecho é a contagem de
 * faces; a corrida entra uma vez, multiplicando.
 *
 * Por isso ela vem por parâmetro, como em `elementVolume`: na viga a corrida que vale é a
 * soma dos trechos (`elementRun`), e não o número digitado ao lado dela.
 */
export function elementEspala(
  element: Pick<StructuralElement, 'kind' | 'length' | 'sectionWidth' | 'sectionDepth'>,
  segments: readonly Pick<ElementSegment, 'wallId' | 'orientation' | 'advance' | 'advanceSide'>[],
  walls: ReadonlyMap<WallId, Pick<Wall, 'thickness'>>,
  run = element.length,
): number {
  const faces = segments.reduce((total, segment) => {
    const wall = walls.get(segment.wallId);
    if (wall === undefined) return total;
    return (
      total + espalaFaces(element, segment, wall, 'A') + espalaFaces(element, segment, wall, 'B')
    );
  }, 0);
  return roundQuantity(faces * run, GEOMETRY_DECIMALS);
}

/**
 * A área das faces do elemento — a fôrma e o revestimento que a peça pede (Fase 24, fatia 10).
 *
 * **Sempre as mesmas faces, onde quer que a peça esteja na parede**: o pilar tem **quatro**
 * (o perímetro da seção × a altura) e a viga tem **três** — as duas laterais e o fundo, o
 * comprimento × (2 × a altura da seção + a largura). O topo da viga fica sob a laje e não
 * leva fôrma nem reboco de ninguém.
 *
 * A posição na parede **não desconta face**. Ela só entra na espala (`elementEspala`), que é
 * o arremate do que sobra para dentro do ambiente: a fôrma do pilar encostado na parede
 * continua sendo de quatro faces, e descontar a que encosta daria ao orçamento uma fôrma que
 * a obra não monta.
 *
 * A corrida vem por parâmetro, como em `elementVolume`: na viga é a soma dos trechos.
 */
export function elementFaces(
  element: Pick<StructuralElement, 'kind' | 'sectionWidth' | 'sectionDepth' | 'length'> &
    Partial<Pick<StructuralElement, 'outline'>>,
  run = element.length,
): number {
  if (element.outline !== undefined) {
    // Pilar: o perímetro do contorno × a altura. Viga: as laterais — o perímetro sem as duas
    // pontas, × a altura da seção — e o fundo, que é a área do contorno. No retângulo dá o
    // mesmo `(2 × altura + largura) × comprimento` de sempre.
    const perimetro = polygonPerimeter(element.outline);
    const faces =
      element.kind === 'PILAR'
        ? perimetro * run
        : Math.max(0, perimetro - 2 * element.sectionWidth) * element.sectionDepth +
          exactPolygonArea(element.outline);
    return roundQuantity(faces, GEOMETRY_DECIMALS);
  }
  const contorno =
    element.kind === 'PILAR'
      ? 2 * (element.sectionWidth + element.sectionDepth)
      : 2 * element.sectionDepth + element.sectionWidth;
  return roundQuantity(contorno * run, GEOMETRY_DECIMALS);
}

/**
 * A área de fundo da viga — largura × comprimento, o que o escoramento sustenta.
 *
 * É a face que as faces da viga já contam, separada porque o serviço que a mede não é o da
 * fôrma: o escoramento escora o **fundo**, e as laterais se apoiam nele. O pilar não tem
 * fundo — ele é vertical —, e devolve zero para que a soma por peça continue fechando.
 */
export function elementBottom(
  element: Pick<StructuralElement, 'kind' | 'sectionWidth' | 'length'> &
    Partial<Pick<StructuralElement, 'outline'>>,
  run = element.length,
): number {
  if (element.kind !== 'VIGA') return 0;
  if (element.outline !== undefined) {
    return roundQuantity(exactPolygonArea(element.outline), GEOMETRY_DECIMALS);
  }
  return roundQuantity(element.sectionWidth * run, GEOMETRY_DECIMALS);
}

/** `"Pilar 20 × 40"` — a seção em centímetros, que é como a obra chama. */
export function describeSection(
  element: Pick<StructuralElement, 'kind' | 'name' | 'sectionWidth' | 'sectionDepth'>,
): string {
  const cm = (metros: number): string => String(Math.round(metros * 100));
  const nome = element.name ?? STRUCTURAL_ELEMENT_KIND_LABEL[element.kind];
  return `${nome} ${cm(element.sectionWidth)} × ${cm(element.sectionDepth)}`;
}
